import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { mockMutualFunds } from '../../utils/mockMutualFunds';
import styles from './MutualFundsPage.module.css';
import { 
  FaChartLine, FaShieldAlt, FaStar, 
  FaSearch, FaBolt, FaGlobe
} from 'react-icons/fa';

const MutualFundsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredFunds = useMemo(() => {
    return mockMutualFunds.filter(fund =>
      fund.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  const featuredFunds = filteredFunds.slice(0, 6);

  const totalFunds = filteredFunds.length;
  const avgReturn = totalFunds > 0
    ? (filteredFunds.reduce((s, f) => s + (f.returns['3Y'] || 0), 0) / totalFunds).toFixed(2)
    : '0';

  const renderStars = (rating) => (
    <div className={styles.rating}>
      {[...Array(5)].map((_, i) => (
        <FaStar key={i} className={i < rating ? styles.starFilled : styles.starEmpty} />
      ))}
    </div>
  );

  return (
    <div className={styles.container}>
      {/* Top Search Bar */}
      <div className={styles.topSection}>
        <div className={styles.searchBox}>
          <FaSearch />
          <input
            type="text"
            placeholder="Search mutual funds..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Overview Stats */}
      <div className={styles.overviewStatsGrid}>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Available Funds</span>
          <span className={styles.statValue}>{totalFunds}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Avg 3Y Return</span>
          <span className={`${styles.statValue} ${styles.positive}`}>{avgReturn}%</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Top Rated</span>
          <span className={styles.statValue}>12 Funds</span>
        </div>
      </div>

      {/* Featured Section */}
      <div className={styles.sectionHeader}>
        <h3>Popular Funds</h3>
        <Link to="/mutual-funds/all" className={styles.viewAll}>View All</Link>
      </div>

      <div className={styles.horizontalScroll}>
        {featuredFunds.map(fund => (
          <Link key={fund.id} to={`/mutual-funds/invest/${fund.id}`} className={styles.fundCard}>
            <div className={styles.cardTop}>
              <div className={styles.fundLogo}>{fund.name.charAt(0)}</div>
              <div className={styles.fundMainInfo}>
                <p className={styles.fundName}>{fund.name}</p>
                <p className={styles.fundCat}>{fund.category}</p>
              </div>
            </div>

            <div className={styles.returnsBadge}>
              <span className={styles.returnVal}>{fund.returns['3Y'] || 'NA'}%</span>
              <span className={styles.returnPeriod}>3Y Returns</span>
            </div>

            <div className={styles.cardBottom}>
              {renderStars(fund.rating)}
              <span className={styles.riskTag}>Moderate Risk</span>
            </div>
          </Link>
        ))}
      </div>

      {/* Collections Section */}
      <div className={styles.sectionHeader}>
        <h3>Handpicked Collections</h3>
      </div>

      <div className={styles.collectionsGrid}>
        <Link to="/mutual-funds/collection/high-return" className={styles.collectionCard}>
          <FaChartLine />
          <span className={styles.collectionTitle}>High Return</span>
          <span className={styles.collectionDesc}>Best performing in 3 years</span>
        </Link>
        <Link to="/mutual-funds/collection/tax-saving" className={styles.collectionCard}>
          <FaShieldAlt />
          <span className={styles.collectionTitle}>Tax Saving</span>
          <span className={styles.collectionDesc}>Save tax under 80C</span>
        </Link>
        <Link to="/mutual-funds/collection/top-rated" className={styles.collectionCard}>
          <FaBolt />
          <span className={styles.collectionTitle}>Top Rated</span>
          <span className={styles.collectionDesc}>5 star rated funds</span>
        </Link>
        <Link to="/mutual-funds/collection/international" className={styles.collectionCard}>
          <FaGlobe />
          <span className={styles.collectionTitle}>International</span>
          <span className={styles.collectionDesc}>Invest in global markets</span>
        </Link>
      </div>
    </div>
  );
};

export default MutualFundsPage;